import * as React from 'react';
import { User, Group, WorkArea } from '../../../services/permissionService';
import { formatUserDisplayName } from '../../../utils/permissionUtils';
import styles from './AdminOverview.module.css';

interface BulkOperationsModalProps {
  availableUsers: User[];
  availableGroups: Group[];
  availableWorkAreas: WorkArea[];
  selectedUserIds: number[];
  selectedGroupIds: number[];
  hasUnrestrictedAccess: boolean;
  selectedWorkAreaIds: number[];
  onUserIdsChange: (userIds: number[]) => void;
  onGroupIdsChange: (groupIds: number[]) => void;
  onUnrestrictedAccessChange: (hasAccess: boolean) => void;
  onWorkAreaIdsChange: (workAreaIds: number[]) => void;
  onUpdate: () => Promise<void>;
  onClose: () => void;
  loading: boolean;
}

export function BulkOperationsModal({
  availableUsers,
  availableGroups,
  availableWorkAreas,
  selectedUserIds,
  selectedGroupIds,
  hasUnrestrictedAccess,
  selectedWorkAreaIds,
  onUserIdsChange,
  onGroupIdsChange,
  onUnrestrictedAccessChange,
  onWorkAreaIdsChange,
  onUpdate,
  onClose,
  loading
}: BulkOperationsModalProps): React.JSX.Element {

  function handleUserToggle(userId: number): void {
    onUserIdsChange(
      selectedUserIds.includes(userId)
        ? selectedUserIds.filter(id => id !== userId)
        : [...selectedUserIds, userId]
    );
  }

  function handleGroupToggle(groupId: number): void {
    onGroupIdsChange(
      selectedGroupIds.includes(groupId)
        ? selectedGroupIds.filter(id => id !== groupId)
        : [...selectedGroupIds, groupId]
    );
  }

  function handleWorkAreaToggle(workAreaId: number): void {
    onWorkAreaIdsChange(
      selectedWorkAreaIds.includes(workAreaId)
        ? selectedWorkAreaIds.filter(id => id !== workAreaId)
        : [...selectedWorkAreaIds, workAreaId]
    );
  }

  function handleUnrestrictedAccessToggle(hasAccess: boolean): void {
    onUnrestrictedAccessChange(hasAccess);
    if (hasAccess) {
      onWorkAreaIdsChange([]);
    }
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainerWide}>
        <h2 className={styles.modalTitle}>Hinzufügen</h2>

        <div className={styles.modalSection}>
          <h3 className={styles.modalSectionTitle}>
            1. Select Users ({selectedUserIds.length} selected)
          </h3>
          <div className={styles.checkboxListCompact}>
            {availableUsers.map(user => (
              <label
                key={user.id}
                className={`${styles.checkboxItemCompact} ${selectedUserIds.includes(user.id) ? styles.checkboxItemSelected : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selectedUserIds.includes(user.id)}
                  onChange={() => handleUserToggle(user.id)}
                  style={{ margin: 0 }}
                />
                <span>{formatUserDisplayName(user)}</span>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.modalSection}>
          <h3 className={styles.modalSectionTitle}>2. Select Groups (optional)</h3>
          <div className={styles.checkboxListShort}>
            {availableGroups.map(group => (
              <label
                key={group.id}
                className={`${styles.checkboxItemCompact} ${selectedGroupIds.includes(group.id) ? styles.checkboxItemSelected : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selectedGroupIds.includes(group.id)}
                  onChange={() => handleGroupToggle(group.id)}
                  style={{ margin: 0 }}
                />
                <span>{group.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.modalSection}>
          <h3 className={styles.modalSectionTitle}>3. Set Work Area Access</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            <label className={`${styles.radioItem} ${hasUnrestrictedAccess ? styles.checkboxItemSelectedGreen : ''}`}>
              <input
                type="radio"
                checked={hasUnrestrictedAccess}
                onChange={() => handleUnrestrictedAccessToggle(true)}
                style={{ margin: 0 }}
              />
              <span>Unrestricted access to all work areas</span>
            </label>
            <label className={`${styles.radioItem} ${!hasUnrestrictedAccess ? styles.checkboxItemSelectedAlt : ''}`}>
              <input
                type="radio"
                checked={!hasUnrestrictedAccess}
                onChange={() => handleUnrestrictedAccessToggle(false)}
                style={{ margin: 0 }}
              />
              <span>Restricted to specific work areas</span>
            </label>
          </div>

          {!hasUnrestrictedAccess && (
            <div className={styles.checkboxListShort}>
              {availableWorkAreas.map(workArea => (
                <label
                  key={workArea.id}
                  className={`${styles.checkboxItemCompact} ${selectedWorkAreaIds.includes(workArea.id) ? styles.checkboxItemSelectedAlt : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedWorkAreaIds.includes(workArea.id)}
                    onChange={() => handleWorkAreaToggle(workArea.id)}
                    style={{ margin: 0 }}
                  />
                  <span>{workArea.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className={styles.buttonRow}>
          <button onClick={onClose} disabled={loading} className={styles.cancelButton}>
            Cancel
          </button>
          <button
            onClick={onUpdate}
            disabled={loading || selectedUserIds.length === 0}
            className={selectedUserIds.length > 0 ? styles.submitButtonBlue : styles.submitButtonDisabled}
          >
            {loading ? 'Updating...' : 'Apply Changes'}
          </button>
        </div>

        <button onClick={onClose} className={styles.modalClose} aria-label="Close">
          &times;
        </button>
      </div>
    </div>
  );
}
